/* === AgToggleGroup — Anagogia Design System === */

import { Children, cloneElement, isValidElement } from 'react';
import styles from './AgToggleGroup.module.css';

/* ------------------------------------------------------------------ */
/*  AgToggleButton                                                     */
/* ------------------------------------------------------------------ */

interface AgToggleButtonProps {
  /** Value associated with this option */
  value: string;
  disabled?: boolean;
  /** Infobulle native : dit, par exemple, pourquoi une option est inerte (fumée D4 du 24/10/2026). */
  title?: string;
  /** Description accessible (par exemple, pourquoi l'option est inerte), lue au clavier et au toucher. */
  'aria-describedby'?: string;
  children: React.ReactNode;
  className?: string;
  /** @internal — set by AgToggleGroup parent */
  _active?: boolean;
  /** @internal */
  _onClick?: (value: string) => void;
  /** @internal */
  _size?: 'sm' | 'md' | 'lg';
}

/**
 * Bouton individuel d'un AgToggleGroup.
 * @category Boutons
 */
function AgToggleButton({
  value,
  disabled = false,
  title,
  'aria-describedby': ariaDescribedBy,
  children,
  className,
  _active = false,
  _onClick,
  _size = 'md',
}: AgToggleButtonProps) {
  const handleClick = () => {
    if (!disabled && _onClick) _onClick(value);
  };

  const cls = [
    styles.button,
    styles[_size],
    _active ? styles.active : undefined,
    disabled ? styles.disabled : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      className={cls}
      role="radio"
      type="button"
      aria-checked={_active}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      title={title}
      aria-describedby={ariaDescribedBy}
      onClick={handleClick}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  AgToggleGroup                                                      */
/* ------------------------------------------------------------------ */

interface AgToggleGroupProps {
  /** Currently selected value */
  value: string;
  /** Called when selection changes */
  onChange: (value: string) => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Nom accessible du groupe (ce que le choix règle), porté par le `radiogroup`. */
  'aria-label'?: string;
  children: React.ReactNode;
}

/**
 * Groupe de boutons à bascule pour choix exclusif ou multiple.
 * @category Boutons
 */
function AgToggleGroup({
  value,
  onChange,
  size = 'md',
  className,
  'aria-label': ariaLabel,
  children,
}: AgToggleGroupProps) {
  const cls = [styles.group, className].filter(Boolean).join(' ');

  return (
    <div className={cls} role="radiogroup" aria-label={ariaLabel}>
      {Children.map(children, (child) => {
        if (!isValidElement(child)) return child;
        const childValue = (child as React.ReactElement<AgToggleButtonProps>)
          .props.value;
        return cloneElement(child as React.ReactElement<AgToggleButtonProps>, {
          _active: childValue === value,
          _onClick: onChange,
          _size: size,
        });
      })}
    </div>
  );
}

export { AgToggleGroup, AgToggleButton };
export type { AgToggleGroupProps, AgToggleButtonProps };
export default AgToggleGroup;
